import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader, Phone, Mail, Building2, User, DollarSign, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { submitToGoogleSheets } from '../utils/googleSheets';

// List of common free email domains to block
const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'live.com', 'msn.com', 'me.com', 'inbox.com', 'mail.ru',
  'googlemail.com', 'yahoo.co.uk', 'yahoo.fr', 'yahoo.co.jp', 'yahoo.com.br',
  'hotmail.co.uk', 'hotmail.fr', 'outlook.fr', 'web.de', 'comcast.net',
  'verizon.net', 'att.net', 'sbcglobal.net', 'cox.net', 'charter.net'
];

// Custom validator for business email
const isBusinessEmail = (email: string) => {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return !FREE_EMAIL_DOMAINS.includes(domain);
};

const formSchema = z.object({
  firstName: z.string()
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .regex(/^[a-zA-Z\s-']+$/, 'First name can only contain letters, spaces, hyphens, and apostrophes'),
  
  lastName: z.string()
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .regex(/^[a-zA-Z\s-']+$/, 'Last name can only contain letters, spaces, hyphens, and apostrophes'),
  
  email: z.string()
    .email('Please enter a valid email address')
    .min(5, 'Email is required')
    .max(100, 'Email must be less than 100 characters')
    .refine(isBusinessEmail, {
      message: 'Please use a business email address. Free email providers (Gmail, Yahoo, etc.) are not accepted.'
    }),
  
  phone: z.string()
    .regex(/^\d{10}$/, 'Please enter a 10-digit phone number without spaces or special characters'),
  
  company: z.string()
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters')
    .optional(),
  
  specialty: z.string()
    .min(2, 'Please select a specialty'),
  
  otherSpecialty: z.string()
    .min(2, 'Please specify your specialty')
    .max(100, 'Specialty must be less than 100 characters')
    .optional()
    .nullable(),
  
  practiceSize: z.string()
    .min(1, 'Please select your practice size'),
  
  budget: z.string()
    .min(1, 'Please select your budget range'),
  
  message: z.string()
    .min(20, 'Please provide more details (minimum 20 characters)')
    .max(2000, 'Message must be less than 2000 characters'),
  
  agreeToTerms: z.boolean()
    .refine(val => val === true, {
      message: 'You must agree to the terms and conditions'
    })
});

type FormData = z.infer<typeof formSchema>;

interface ContactFormProps {
  onFormStart?: () => void;
  onFormComplete?: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ onFormStart, onFormComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [showOtherSpecialty, setShowOtherSpecialty] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    trigger,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange'
  });

  const specialty = watch('specialty');

  useEffect(() => {
    const isOther = specialty === 'other';
    setShowOtherSpecialty(isOther);
    
    if (!isOther) {
      setValue('otherSpecialty', null, { shouldValidate: true });
    }
  }, [specialty, setValue]);

  useEffect(() => {
    const form = document.querySelector('form');
    if (form) {
      const handleInteraction = () => onFormStart?.();
      form.addEventListener('focusin', handleInteraction, { once: true });
      return () => form.removeEventListener('focusin', handleInteraction);
    }
  }, [onFormStart]);

  const isStepValid = async (step: number) => {
    let fieldsToValidate: (keyof FormData)[] = [];
    
    switch (step) {
      case 1:
        fieldsToValidate = ['firstName', 'lastName', 'email'];
        break;
      case 2:
        fieldsToValidate = ['phone', 'company'];
        break;
      case 3:
        fieldsToValidate = ['specialty', 'practiceSize', 'budget', 'message', 'agreeToTerms'];
        if (showOtherSpecialty) fieldsToValidate.push('otherSpecialty');
        break;
    }

    return await trigger(fieldsToValidate);
  };

  const nextStep = async () => {
    if (await isStepValid(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = async (data: FormData) => {
    try {
      setSubmitStatus('submitting');
      setSubmitError(null);
      setDebugInfo(null);

      // Store form data in session storage before submission
      const formData = {
        firstName: data.firstName,
        email: data.email,
        specialty: data.specialty === 'other' ? data.otherSpecialty : data.specialty,
        submissionTime: new Date().toISOString()
      };
      
      sessionStorage.setItem('formSubmission', JSON.stringify(formData));
      
      console.log('Submitting form data:', data);
      
      // Submit the form data
      const response = await submitToGoogleSheets(data);
      console.log('Form submission response:', response);

      if (response.success) {
        setSubmitStatus('success');
        onFormComplete?.();
        
        // Wait 2 seconds to show success message before redirecting
        setTimeout(() => {
          // Navigate directly to confirmation page
          navigate('/contact/confirmation', { replace: true });
          // Force scroll to top immediately
          window.scrollTo(0, 0);
          document.body.scrollTop = 0; // For Safari
        }, 2000);
      } else {
        setDebugInfo(JSON.stringify(response, null, 2));
        throw new Error(response.message || 'Form submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred');
      
      if (error instanceof Error && error.message.includes('server')) {
        navigate('/contact/error', { 
          state: { error: error.message }
        });
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-lg">
      {submitStatus === 'success' ? (
        <div className="text-center py-8">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-2">Thank You!</h3>
          <p className="text-gray-600 mb-6">
            Your form has been submitted successfully. We'll be in touch soon!
          </p>
          <div className="animate-pulse">
            <p className="text-gray-500">Redirecting to confirmation page...</p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {submitError && (
            <div className="bg-red-50 p-4 rounded-md">
              <p className="text-red-800">{submitError}</p>
              {debugInfo && (
                <details className="mt-2">
                  <summary className="text-sm text-red-700 cursor-pointer">Debug Information</summary>
                  <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-auto max-h-40">{debugInfo}</pre>
                </details>
              )}
            </div>
          )}

          <div className="flex items-center justify-between mb-8">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center
                  ${currentStep === step 
                    ? 'bg-blue-600 text-white' 
                    : currentStep > step 
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {currentStep > step ? '✓' : step}
                </div>
                {step < 3 && (
                  <div className={`w-24 h-1 mx-2 ${
                    currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Basic Info */}
          <div className={`${currentStep === 1 ? 'block' : 'hidden'}`}>
            <div className="space-y-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    id="firstName"
                    {...register('firstName')}
                    className={`form-input w-full pl-10 pr-4 py-2 ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="Enter your first name"
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    id="lastName"
                    {...register('lastName')}
                    className={`form-input w-full pl-10 pr-4 py-2 ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Enter your last name"
                  />
                </div>
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Business Email * <span className="text-xs text-gray-500">(free email providers not accepted)</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="email"
                    id="email"
                    {...register('email')}
                    className={`form-input w-full pl-10 pr-4 py-2 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="Enter your business email address"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-start">
                    <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                    <span>{errors.email.message}</span>
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  We only accept business email addresses to ensure proper delivery of important information.
                </p>
              </div>
            </div>
          </div>

          {/* Step 2: Contact Info */}
          <div className={`${currentStep === 2 ? 'block' : 'hidden'}`}>
            <div className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number * (10 digits)
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="tel"
                    id="phone"
                    {...register('phone')}
                    className={`form-input w-full pl-10 pr-4 py-2 ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="Enter your phone number"
                  />
                </div>
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-1">
                  Company/Practice Name
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    id="company"
                    {...register('company')}
                    className="form-input w-full pl-10 pr-4 py-2"
                    placeholder="Enter your company name"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Project Details */}
          <div className={`${currentStep === 3 ? 'block' : 'hidden'}`}>
            <div className="space-y-6">
              <div>
                <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-1">
                  Specialty *
                </label>
                <select
                  id="specialty"
                  {...register('specialty')}
                  className={`form-select w-full ${errors.specialty ? 'border-red-500' : ''}`}
                >
                  <option value="">Select Specialty</option>
                  <option value="cardiology">Cardiology</option>
                  <option value="dental">Dental</option>
                  <option value="dermatology">Dermatology</option>
                  <option value="ent">ENT</option>
                  <option value="medspa">Medical Spa</option>
                  <option value="neurology">Neurology</option>
                  <option value="obgyn">OB/GYN</option>
                  <option value="oncology">Oncology</option>
                  <option value="ophthalmology">Ophthalmology</option>
                  <option value="orthopedics">Orthopedics</option>
                  <option value="pediatrics">Pediatrics</option>
                  <option value="plastic-surgery">Plastic Surgery</option>
                  <option value="podiatry">Podiatry</option>
                  <option value="urology">Urology</option>
                  <option value="other">Other</option>
                </select>
                {errors.specialty && (
                  <p className="mt-1 text-sm text-red-600">{errors.specialty.message}</p>
                )}
              </div>

              {showOtherSpecialty && (
                <div>
                  <label htmlFor="otherSpecialty" className="block text-sm font-medium text-gray-700 mb-1">
                    Please specify your specialty *
                  </label>
                  <input
                    type="text"
                    id="otherSpecialty"
                    {...register('otherSpecialty')}
                    className={`form-input w-full ${errors.otherSpecialty ? 'border-red-500' : ''}`}
                    placeholder="Enter your specialty"
                  />
                  {errors.otherSpecialty && (
                    <p className="mt-1 text-sm text-red-600">{errors.otherSpecialty.message}</p>
                  )}
                </div>
              )}

              <div>
                <label htmlFor="practiceSize" className="block text-sm font-medium text-gray-700 mb-1">
                  Practice Size *
                </label>
                <select
                  id="practiceSize"
                  {...register('practiceSize')}
                  className={`form-select w-full ${errors.practiceSize ? 'border-red-500' : ''}`}
                >
                  <option value="">Select Practice Size</option>
                  <option value="Large">Large (11+ providers)</option>
                  <option value="Medium">Medium (6-10 providers)</option>
                  <option value="Small">Small (2-5 providers)</option>
                  <option value="Solo">Solo Practice</option>
                </select>
                {errors.practiceSize && (
                  <p className="mt-1 text-sm text-red-600">{errors.practiceSize.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                  Budget Range *
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <select
                    id="budget"
                    {...register('budget')}
                    className={`form-select w-full pl-10 pr-4 py-2 ${errors.budget ? 'border-red-500' : ''}`}
                  >
                    <option value="">Select Budget Range</option>
                    <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                    <option value="$10,000 - $25,000">$10,000 - $25,000</option>
                    <option value="$25,000 - $50,000">$25,000 - $50,000</option>
                    <option value="$50,000+">$50,000+</option>
                  </select>
                </div>
                {errors.budget && (
                  <p className="mt-1 text-sm text-red-600">{errors.budget.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  Project Details *
                </label>
                <textarea
                  id="message"
                  {...register('message')}
                  rows={4}
                  className={`form-input w-full ${errors.message ? 'border-red-500' : ''}`}
                  placeholder="Please describe your project needs and goals..."
                  style={{ minHeight: "120px" }}
                />
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              <div className="flex items-start mt-4">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    {...register('agreeToTerms')}
                    className={`form-checkbox h-4 w-4 ${errors.agreeToTerms ? 'border-red-500' : ''}`}
                  />
                </div>
                <div className="ml-3">
                  <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                    I agree to the terms and conditions *
                  </label>
                  {errors.agreeToTerms && (
                    <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between pt-6 mt-6">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={prevStep}
                className="bg-gray-200 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>
            )}
            
            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                className="ml-auto bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                Next
                <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitStatus === 'submitting' || !isValid}
                className="ml-auto bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {submitStatus === 'submitting' ? (
                  <>
                    <Loader className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit
                    <svg className="w-5 h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default ContactForm;